import { getEnv } from "../config/env";

// services/AudienceApiService.js - UPDATED: Added segment API functionality
class AudienceApiService {
	constructor() {
		// Use the same proxy URL pattern as your working services
		const env = getEnv();
		this.proxyUrl = env.REACT_APP_PROXY;
		this.baseURL = this.proxyUrl;
		this.timeout = 300000;

		// Cache for suggestions and other data
		this.cache = new Map();
		this.cacheTimeout = 10 * 60 * 1000; // 10 minutes

		// ✅ Filter values cache
		this.filterValuesCache = null;
		this.filterValuesCacheExpiry = null;
		this.filterValuesCacheTimeout = 30 * 60 * 1000; // 30 minutes

		// 🆕 Segment data cache
		this.segmentCache = new Map();
		this.segmentCacheTimeout = 15 * 60 * 1000; // 15 minutes

		console.log("💥 AudienceApiService initializing with proxy support...");
	}

	setProxyUrl(url) {
		const normalized = url.replace(/\/$/, "");
		this.proxyUrl = normalized;
		this.baseURL = normalized;
	}

	// ==================== AUTHENTICATION METHODS ====================

	/**
	 * Get authentication token from the main ApiService (same pattern as AppApiService)
	 */
	getSharedAuthToken() {
		// Try to get token from main ApiService first
		if (
			window.apiService &&
			window.apiService.tokens &&
			window.apiService.tokens.idToken
		) {
			return window.apiService.tokens.idToken;
		}

		// Fallback to localStorage
		return (
			localStorage.getItem("google_id_token") ||
			localStorage.getItem("authToken")
		);
	}

	/**
	 * Check if we have valid authentication via main ApiService
	 */
	isAuthenticated() {
		// Check main ApiService first
		if (
			window.apiService &&
			typeof window.apiService.isAuthenticated === "function"
		) {
			try {
				const mainServiceAuth = window.apiService.isAuthenticated();
				if (!mainServiceAuth) {
					this.redirectToLogin();
					return false;
				}
				return mainServiceAuth;
			} catch (error) {
				console.warn("⚠️ Main ApiService auth check failed:", error);
			}
		}

		// Fallback to token check
		const token = this.getSharedAuthToken();
		if (!token) {
			this.redirectToLogin();
			return false;
		}

		return this.checkTokenExpiration(token);
	}

	/**
	 * Redirect to login page when token expires (same as AppApiService)
	 */
	redirectToLogin() {
		console.log("🔐 Audience API: Token expired, redirecting to login page...");

		// Clear expired tokens
		this.clearExpiredTokens();

		// Try to use main ApiService logout if available
		if (window.apiService && typeof window.apiService.signOut === "function") {
			try {
				window.apiService.signOut();
			} catch (error) {
				console.warn("⚠️ Main ApiService signOut failed:", error);
			}
		}

		// Redirect to login page
		if (typeof window !== "undefined") {
			if (
				window.location.pathname !== "/" &&
				window.location.pathname !== "/login"
			) {
				window.location.href = "/";
			}
		}
	}

	/**
	 * Clear expired tokens from storage
	 */
	clearExpiredTokens() {
		try {
			localStorage.removeItem("google_id_token");
			localStorage.removeItem("authToken");
			localStorage.removeItem("refresh_token");
			console.log("🧹 Audience API: Expired tokens cleared from storage");
		} catch (error) {
			console.warn("⚠️ Failed to clear tokens:", error);
		}
	}

	/**
	 * Check if token is expired and handle accordingly
	 */
	checkTokenExpiration(token) {
		if (!token) return false;

		try {
			const payload = this.parseJWT(token);
			if (!payload || !payload.exp) return false;

			const now = Date.now();
			const expiration = payload.exp * 1000;
			const isExpired = expiration <= now;

			if (isExpired) {
				console.log(
					"🔐 Audience API: Token has expired, initiating redirect...",
				);
				this.redirectToLogin();
				return false;
			}

			return true;
		} catch (error) {
			console.warn("⚠️ Audience API: Token validation failed:", error);
			this.redirectToLogin();
			return false;
		}
	}

	/**
	 * Parse JWT token
	 */
	parseJWT(token) {
		try {
			const base64Url = token.split(".")[1];
			const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
			const jsonPayload = decodeURIComponent(
				Array.from(atob(base64))
					.map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
					.join(""),
			);
			return JSON.parse(jsonPayload);
		} catch {
			return null;
		}
	}

	/**
	 * Get user info from main ApiService
	 */
	getUser() {
		// Check authentication first
		if (!this.isAuthenticated()) {
			return null;
		}

		// Try main ApiService first
		if (window.apiService && typeof window.apiService.getUser === "function") {
			try {
				return window.apiService.getUser();
			} catch (error) {
				console.warn("⚠️ Main ApiService getUser failed:", error);
			}
		}

		// Fallback to parsing token manually
		const token = this.getSharedAuthToken();
		if (!token) return null;

		try {
			const payload = this.parseJWT(token);
			return payload
				? {
						id: payload.sub,
						email: payload.email,
						name: payload.name,
						picture: payload.picture,
						email_verified: payload.email_verified,
					}
				: null;
		} catch {
			return null;
		}
	}

	// ==================== REQUEST HEADERS & API CALLS ====================

	/**
	 * Get headers with shared authentication
	 */
	getHeaders() {
		const headers = {
			Accept: "application/json",
			"Content-Type": "application/json",
		};

		const token = this.getSharedAuthToken();
		if (token && this.checkTokenExpiration(token)) {
			headers["Authorization"] = `Bearer ${token}`;
		}

		return headers;
	}

	/**
	 * Make authenticated API request with silent redirect on auth failure
	 */
	async makeRequest(endpoint, options = {}) {
		try {
			// Check authentication - this will redirect if expired
			if (!this.isAuthenticated()) {
				// isAuthenticated() will handle the redirect, so we just return
				return new Promise(() => {}); // Never resolves to prevent further execution
			}

			const headers = this.getHeaders();
			const normalizedEndpoint = endpoint.startsWith("/")
				? endpoint
				: `/${endpoint}`;

			// Route audience requests through the proxy
			const url = `${this.baseURL}${normalizedEndpoint}`;

			const requestOptions = {
				method: "GET",
				...options,
				headers: { ...headers, ...options.headers },
				mode: "cors",
				credentials: "omit",
				cache: "no-cache",
			};

			console.log(`🌐 Audience API Request: ${requestOptions.method} ${url}`);

			const response = await fetch(url, {
				...requestOptions,
				signal: AbortSignal.timeout(this.timeout),
			});

			// Handle auth errors with immediate redirect (no retries)
			if (response.status === 401 || response.status === 403) {
				console.log(
					"🔐 Audience API: Authentication failed, redirecting to login...",
				);
				this.redirectToLogin();
				return new Promise(() => {}); // Never resolves
			}

			if (!response.ok) {
				const errorText = await response.text().catch(() => "Unknown error");
				throw new Error(
					`HTTP ${response.status}: ${response.statusText}. ${errorText}`,
				);
			}

			const data = await response.json();
			console.log("✅ Audience API Response received successfully");

			return {
				success: true,
				data: data,
				statusCode: response.status,
			};
		} catch (error) {
			console.error("❌ Audience API Error:", error);

			let errorMessage = error.message;

			if (
				error.name === "TypeError" &&
				error.message.includes("Failed to fetch")
			) {
				errorMessage =
					"Network error: Unable to connect to Audience API. Please check your internet connection and try again.";
			} else if (error.message.includes("CORS")) {
				errorMessage =
					"CORS error: Audience API access blocked. Please contact support.";
			} else if (
				error.message.includes("401") ||
				error.message.includes("403")
			) {
				// Auth errors should trigger redirect
				this.redirectToLogin();
				return new Promise(() => {}); // Never resolves
			}

			return {
				success: false,
				error: errorMessage,
				statusCode: error.status || 500,
			};
		}
	}

	// ==================== 🆕 SEGMENT API METHODS ====================

	/**
	 * 🆕 Fetch segment IDs for a specific audience ID
	 */
	async getAudienceSegments(audienceId, forceRefresh = false) {
		try {
			console.log("🧩 ===== FETCHING AUDIENCE SEGMENTS =====");
			console.log("🆔 Audience ID:", audienceId);

			if (!audienceId) {
				throw new Error("Audience ID is required");
			}

			// Check cache first (unless force refresh)
			const cacheKey = `segments_${audienceId}`;
			const now = Date.now();

			if (!forceRefresh && this.segmentCache.has(cacheKey)) {
				const cached = this.segmentCache.get(cacheKey);
				if (
					cached.timestamp &&
					now - cached.timestamp < this.segmentCacheTimeout
				) {
					console.log("✅ Using cached segment data");
					return {
						success: true,
						data: cached.data,
						audienceId: audienceId,
						fromCache: true,
						cachedAt: new Date(cached.timestamp).toISOString(),
					};
				}
			}

			console.log("🌐 Fetching fresh segment data from API...");

			// Check authentication first
			if (!this.isAuthenticated()) {
				console.log("🔐 Not authenticated, redirecting...");
				return new Promise(() => {}); // Never resolves after redirect
			}

			// Make direct API call to the segment endpoint
			// Note: Using direct URL since this doesn't go through the proxy
			const env = getEnv();
			const segmentApiUrl = `${env.REACT_APP_PROXY}/segment/${audienceId}`;

			const headers = this.getHeaders();

			console.log(`🌐 Segment API Request: GET ${segmentApiUrl}`);

			const response = await fetch(segmentApiUrl, {
				method: "GET",
				headers: headers,
				mode: "cors",
				credentials: "omit",
				cache: "no-cache",
				signal: AbortSignal.timeout(this.timeout),
			});

			// Handle auth errors with immediate redirect
			if (response.status === 401 || response.status === 403) {
				console.log(
					"🔐 Segment API: Authentication failed, redirecting to login...",
				);
				this.redirectToLogin();
				return new Promise(() => {}); // Never resolves
			}

			if (!response.ok) {
				const errorText = await response.text().catch(() => "Unknown error");
				throw new Error(
					`HTTP ${response.status}: ${response.statusText}. ${errorText}`,
				);
			}

			const data = await response.json();
			console.log("✅ Segment API Response received successfully");

			// Process and normalize the segment data
			const normalizedSegments = this.normalizeSegmentData(data);

			// Cache the result
			this.segmentCache.set(cacheKey, {
				data: normalizedSegments,
				timestamp: now,
			});

			console.log("📊 Segment data cached successfully");

			return {
				success: true,
				data: normalizedSegments,
				audienceId: audienceId,
				fromCache: false,
				fetchedAt: new Date().toISOString(),
			};
		} catch (error) {
			console.error("❌ ===== SEGMENT API ERROR =====");
			console.error("❌ Error message:", error.message);

			// Check for authentication-related errors
			if (
				error.message.includes("401") ||
				error.message.includes("403") ||
				error.message.includes("Unauthorized")
			) {
				console.log("🔐 Authentication error, redirecting...");
				this.redirectToLogin();
				return new Promise(() => {}); // Never resolves
			}

			return {
				success: false,
				error: error.message || "Failed to fetch audience segments",
				audienceId: audienceId,
				statusCode: error.status || 500,
			};
		}
	}

	/**
	 * 🆕 Normalize segment data from API response
	 */
	normalizeSegmentData(data) {
		console.log("🔄 Normalizing segment data...");

		if (!data) {
			console.log("⚠️ No segment data to normalize");
			return [];
		}

		// Handle simple array response (just segment IDs)
		if (Array.isArray(data)) {
			console.log(`✅ Received simple array with ${data.length} segment IDs`);
			return data; // Return the array as-is
		}

		// Handle object response with segments array
		if (data.segments && Array.isArray(data.segments)) {
			console.log(`✅ Received object with ${data.segments.length} segments`);
			return data.segments;
		}

		// Handle single segment ID (string)
		if (typeof data === "string") {
			console.log("✅ Received single segment ID string");
			return [data];
		}

		// If data is a single object, wrap it in an array
		if (typeof data === "object") {
			console.log("✅ Received single segment object");
			return [data];
		}

		console.log("⚠️ Unknown segment data format:", typeof data);
		return [];
	}

	/**
	 * 🆕 Clear segment cache for specific audience or all
	 */
	clearSegmentCache(audienceId = null) {
		if (audienceId) {
			const cacheKey = `segments_${audienceId}`;
			this.segmentCache.delete(cacheKey);
			console.log(`🧹 Segment cache cleared for audience: ${audienceId}`);
		} else {
			this.segmentCache.clear();
			console.log("🧹 All segment cache cleared");
		}
	}

	/**
	 * 🆕 Get segment cache status
	 */
	getSegmentCacheStatus() {
		const now = Date.now();
		const cacheEntries = Array.from(this.segmentCache.entries()).map(
			([key, value]) => {
				const age = now - value.timestamp;
				const isExpired = age >= this.segmentCacheTimeout;
				return {
					key,
					audienceId: key.replace("segments_", ""),
					age: age,
					ageMinutes: Math.round(age / (1000 * 60)),
					isExpired,
					segmentCount: value.data?.segments?.length || 0,
					totalReach: value.data?.summary?.totalReach || 0,
				};
			},
		);

		return {
			totalEntries: cacheEntries.length,
			activeEntries: cacheEntries.filter((entry) => !entry.isExpired).length,
			expiredEntries: cacheEntries.filter((entry) => entry.isExpired).length,
			cacheTimeoutMinutes: Math.round(this.segmentCacheTimeout / (1000 * 60)),
			entries: cacheEntries,
		};
	}

	// ==================== EXISTING METHODS (unchanged) ====================

	// ... (all the existing methods remain the same)

	async getFilterValues(forceRefresh = false) {
		try {
			console.log("🔧 ===== GETTING FILTER VALUES =====");

			// Check cache first (unless force refresh)
			const now = Date.now();
			if (
				!forceRefresh &&
				this.filterValuesCache &&
				this.filterValuesCacheExpiry &&
				now < this.filterValuesCacheExpiry
			) {
				console.log("✅ Using cached filter values");
				return {
					success: true,
					data: this.filterValuesCache,
					fromCache: true,
				};
			}

			console.log("🌐 Fetching fresh filter values from API...");

			// Check authentication first
			if (!this.isAuthenticated()) {
				console.log("🔐 Not authenticated, redirecting...");
				return new Promise(() => {}); // Never resolves after redirect
			}

			const response = await this.makeRequest("/audience/get-filter-values", {
				method: "GET",
			});

			if (response.success) {
				console.log("✅ Filter values API request successful");

				// Cache the response
				this.filterValuesCache = response.data;
				this.filterValuesCacheExpiry = now + this.filterValuesCacheTimeout;

				console.log("📊 Filter values cached successfully");
				console.log(
					"📋 Available filter categories:",
					Object.keys(response.data?.audienceFilterValues || {}),
				);

				return {
					success: true,
					data: response.data,
					fromCache: false,
				};
			} else {
				console.error("❌ Filter values API request failed:", response);
				return response;
			}
		} catch (error) {
			console.error("❌ ===== FILTER VALUES ERROR =====");
			console.error("❌ Error message:", error.message);

			// Check for authentication-related errors
			if (
				error.message.includes("401") ||
				error.message.includes("403") ||
				error.message.includes("Unauthorized")
			) {
				console.log("🔐 Authentication error, redirecting...");
				this.redirectToLogin();
				return new Promise(() => {}); // Never resolves
			}

			return {
				success: false,
				error: error.message || "Failed to fetch filter values",
				statusCode: error.status || 500,
			};
		}
	}

	async getFilterValuesByCategory(category, forceRefresh = false) {
		try {
			const response = await this.getFilterValues(forceRefresh);

			if (!response.success) {
				return response;
			}

			const filterValues = response.data?.audienceFilterValues || {};

			if (!filterValues[category]) {
				console.warn(
					`⚠️ Filter category '${category}' not found. Available categories:`,
					Object.keys(filterValues),
				);
				return {
					success: true,
					data: [],
					category: category,
					available: Object.keys(filterValues),
				};
			}

			return {
				success: true,
				data: filterValues[category],
				category: category,
				fromCache: response.fromCache,
			};
		} catch (error) {
			console.error(
				`❌ Error getting filter values for category '${category}':`,
				error,
			);
			return {
				success: false,
				error: error.message || `Failed to get filter values for ${category}`,
				category: category,
			};
		}
	}

	async getAvailableFilterCategories(forceRefresh = false) {
		try {
			const response = await this.getFilterValues(forceRefresh);

			if (!response.success) {
				return response;
			}

			const filterValues = response.data?.audienceFilterValues || {};
			const categories = Object.keys(filterValues);

			return {
				success: true,
				data: categories,
				details: Object.keys(filterValues).map((category) => ({
					name: category,
					count: Array.isArray(filterValues[category])
						? filterValues[category].length
						: 0,
					type: Array.isArray(filterValues[category])
						? "array"
						: typeof filterValues[category],
				})),
				fromCache: response.fromCache,
			};
		} catch (error) {
			console.error("❌ Error getting available filter categories:", error);
			return {
				success: false,
				error: error.message || "Failed to get available filter categories",
			};
		}
	}

	clearFilterValuesCache() {
		this.filterValuesCache = null;
		this.filterValuesCacheExpiry = null;
		console.log("🧹 Filter values cache cleared");
	}

	getFilterValuesCacheStatus() {
		const now = Date.now();
		const hasCache = !!this.filterValuesCache;
		const isExpired =
			!this.filterValuesCacheExpiry || now >= this.filterValuesCacheExpiry;

		return {
			hasCache,
			isExpired,
			cacheAge: this.filterValuesCacheExpiry
				? now - (this.filterValuesCacheExpiry - this.filterValuesCacheTimeout)
				: null,
			expiresIn: this.filterValuesCacheExpiry
				? Math.max(0, this.filterValuesCacheExpiry - now)
				: null,
			categories: hasCache
				? Object.keys(this.filterValuesCache?.audienceFilterValues || {})
				: [],
		};
	}

	async searchAudiences(completePayload) {
		try {
			console.log("🔍 ===== AUDIENCE SEARCH API =====");
			console.log("📤 Received complete payload:", {
				hasQuery:
					Array.isArray(completePayload.query) &&
					completePayload.query.length > 0,
				queryCount: completePayload.query?.length || 0,
				hasFilters: !!completePayload.filters,
				hasAudienceVector: !!completePayload.filters?.audienceVector,
				hasAudienceMetrics: !!completePayload.filters?.audienceMetrics,
				hasCumulativeDataFilters:
					!!completePayload.filters?.cumulativeDataFilters,
				sortBy: completePayload.sortBy,
				view: completePayload.view,
				noOfResult: completePayload.noOfResult,
			});

			// Check authentication first - will redirect if expired
			if (!this.isAuthenticated()) {
				console.log("🔐 Not authenticated, redirecting...");
				return new Promise(() => {}); // Never resolves after redirect
			}

			// ✅ SIMPLE VALIDATION: Just check basic structure
			if (!completePayload || typeof completePayload !== "object") {
				throw new Error("Invalid payload: Must be an object");
			}

			if (!Array.isArray(completePayload.query)) {
				throw new Error("Invalid payload: query must be an array");
			}

			if (!Array.isArray(completePayload.excludeQuery)) {
				throw new Error("Invalid payload: excludeQuery must be an array");
			}

			if (
				!completePayload.filters ||
				typeof completePayload.filters !== "object"
			) {
				throw new Error("Invalid payload: filters must be an object");
			}

			console.log("✅ Payload validation passed, making API request...");

			// ✅ DIRECT API CALL: No payload modification, just send what we received
			const response = await this.makeRequest("/audience/search", {
				method: "POST",
				body: JSON.stringify(completePayload),
			});

			console.log("🔥 ===== RAW API RESPONSE =====");
			console.log("🔥 Response success:", response.success);
			console.log("🔥 Response status code:", response.statusCode);

			if (response.success) {
				console.log("✅ API request successful, starting normalization...");

				// ✅ NORMALIZE RESPONSE: Process the API response structure
				const normalizedData = this.normalizeAudienceResponse(response.data);
				console.log("✅ Normalization complete");

				const finalResponse = {
					success: true,
					data: normalizedData.audiences, // Return audiences array
					totalRecords: normalizedData.totalCount,
					totalResults: normalizedData.totalCount,
					searchTime: normalizedData.searchTime,
					metadata: normalizedData.metadata,
					statusCode: response.statusCode,
					// Include original for debugging and backward compatibility
					originalResponse: response.data,
					// Add response format detection for debugging
					responseFormat: response.data.body
						? "legacy_with_body"
						: "new_direct_keys",
				};

				console.log("📊 ===== FINAL RESPONSE STRUCTURE =====");
				console.log("📊 Response format:", finalResponse.responseFormat);
				console.log(
					"📊 Returning audiences array with length:",
					finalResponse.data.length,
				);
				console.log("✅ Search successful, returning normalized data");

				return finalResponse;
			}

			console.error("❌ API request failed:", response);
			return response;
		} catch (error) {
			console.error("❌ ===== AUDIENCE SEARCH ERROR =====");
			console.error("❌ Error message:", error.message);

			// Check for authentication-related errors
			if (
				error.message.includes("401") ||
				error.message.includes("403") ||
				error.message.includes("Unauthorized")
			) {
				console.log("🔐 Authentication error, redirecting...");
				this.redirectToLogin();
				return new Promise(() => {}); // Never resolves
			}

			return {
				success: false,
				error: error.message || "Audience search failed",
				statusCode: error.status || 500,
			};
		}
	}

	normalizeAudienceResponse(data) {
		console.log("🔄 ===== NORMALIZATION PROCESS =====");
		console.log("🔍 Input data keys:", data ? Object.keys(data) : "no data");

		if (!data) {
			console.log("❌ No data provided for normalization");
			return { audiences: [], totalResults: 0, totalCount: 0 };
		}

		let allAudiences = [];
		let totalCount = 0;
		const allCumulativeData = {};
		let allCategoryData = [];
		const searchTermResults = {}; // Track results by search term

		// ✅ Handle both old structure (with body wrapper) and new structure (direct keys)
		const dataToProcess =
			data.body && typeof data.body === "object" ? data.body : data;

		if (dataToProcess && typeof dataToProcess === "object") {
			console.log("✅ Found data structure, analyzing keys...");
			console.log("📊 Data keys:", Object.keys(dataToProcess));

			// ✅ Process ALL dynamic keys (search terms) - exclude statusCode and statusCodeValue
			const dynamicKeys = Object.keys(dataToProcess).filter(
				(key) => !["statusCode", "statusCodeValue"].includes(key),
			);

			if (dynamicKeys.length > 0) {
				console.log(
					`🔄 Processing ${dynamicKeys.length} search term results...`,
				);

				// Process each search term key
				dynamicKeys.forEach((searchTerm, index) => {
					const keyData = dataToProcess[searchTerm];
					console.log(
						`🔍 Processing search term "${searchTerm}" (${index + 1}/${dynamicKeys.length}):`,
						{
							hasData: !!keyData,
							type: typeof keyData,
							hasDataArray:
								keyData && keyData.data && Array.isArray(keyData.data),
							dataLength:
								keyData && keyData.data ? keyData.data.length : "no data",
						},
					);

					if (keyData && typeof keyData === "object") {
						// Check if this key contains the expected structure
						if (keyData.data && Array.isArray(keyData.data)) {
							console.log(
								`✅ Found audience data for "${searchTerm}" with ${keyData.data.length} items`,
							);

							// Add audiences from this search term to the combined results
							const searchTermAudiences = keyData.data;
							allAudiences = allAudiences.concat(searchTermAudiences);

							// Track results by search term for metadata
							searchTermResults[searchTerm] = {
								count: searchTermAudiences.length,
								totalCount: keyData.totalCount || searchTermAudiences.length,
								cumulativeData: keyData.cumulativeData || null,
								categoryData: keyData.categoryData || null,
							};

							// Accumulate total count
							totalCount += keyData.totalCount || searchTermAudiences.length;

							// Merge cumulative data from each search term
							if (keyData.cumulativeData) {
								console.log(`🔄 Merging cumulative data for "${searchTerm}"`);
								allCumulativeData[searchTerm] = keyData.cumulativeData;
							}

							// Merge category data from each search term
							if (keyData.categoryData && Array.isArray(keyData.categoryData)) {
								console.log(`🔄 Merging category data for "${searchTerm}"`);
								const categoriesWithSearchTerm = keyData.categoryData.map(
									(category) => ({
										...category,
										searchTerm: searchTerm,
									}),
								);
								allCategoryData = allCategoryData.concat(
									categoriesWithSearchTerm,
								);
							}

							console.log(
								`📊 Processed "${searchTerm}": ${searchTermAudiences.length} audiences, running total: ${allAudiences.length}`,
							);
						} else {
							console.log(
								`⚠️ No valid audience data found for search term "${searchTerm}"`,
							);
						}
					}
				});

				console.log(
					`✅ Completed processing all search terms. Total audiences: ${allAudiences.length}`,
				);
			}
		}
		// Fallback to other possible response formats
		else if (Array.isArray(data)) {
			console.log("✅ Data is directly an array");
			allAudiences = data;
			totalCount = data.length;
		} else if (data.audiences && Array.isArray(data.audiences)) {
			console.log("✅ Found audiences property");
			allAudiences = data.audiences;
			totalCount = data.totalCount || data.total || data.audiences.length;
		} else if (data.data && Array.isArray(data.data)) {
			console.log("✅ Found data property");
			allAudiences = data.data;
			totalCount = data.totalCount || data.total || data.data.length;
		} else if (data.results && Array.isArray(data.results)) {
			console.log("✅ Found results property");
			allAudiences = data.results;
			totalCount = data.totalCount || data.total || data.results.length;
		} else {
			console.log("❌ Unknown data structure for normalization");
			console.log("❌ Available keys:", Object.keys(data));
		}

		console.log(
			`📈 Raw audiences for normalization: ${allAudiences.length} items`,
		);

		// Remove duplicates based on audienceId
		const uniqueAudiences = [];
		const seenIds = new Set();

		allAudiences.forEach((audience, index) => {
			const audienceId =
				audience.audienceVector?.audienceId ||
				audience.audienceId ||
				audience.id ||
				`audience_${index}`;

			if (!seenIds.has(audienceId)) {
				seenIds.add(audienceId);
				uniqueAudiences.push(audience);
			} else {
				console.log(`🔍 Duplicate audience found and removed: ${audienceId}`);
			}
		});

		console.log(
			`🔄 Deduplication: ${allAudiences.length} → ${uniqueAudiences.length} unique audiences`,
		);

		// Normalize each unique audience item
		console.log("🔄 Starting individual item normalization...");
		const normalizedAudiences = uniqueAudiences
			.map((audience, index) => {
				return this.normalizeAudienceItem(audience);
			})
			.filter(Boolean);

		// Process cumulative and category data
		let processedCumulativeData = null;
		if (Object.keys(allCumulativeData).length > 0) {
			console.log(
				"🔄 Processing combined cumulative data from all search terms",
			);
			processedCumulativeData = {};
			Object.keys(allCumulativeData).forEach((searchTerm) => {
				if (allCumulativeData[searchTerm]) {
					processedCumulativeData[searchTerm] = this.processCumulativeData(
						allCumulativeData[searchTerm],
					);
				}
			});
		}

		let processedCategoryData = null;
		if (allCategoryData.length > 0) {
			console.log("🔄 Processing combined category data from all search terms");
			processedCategoryData = this.processCategoryData(allCategoryData);
		}

		const normalizedResponse = {
			audiences: normalizedAudiences,
			totalResults: totalCount,
			totalCount: totalCount,
			uniqueCount: normalizedAudiences.length,
			searchTime: data.searchTime || null,
			metadata: {
				...data.metadata,
				searchTermResults: searchTermResults,
				cumulativeData: processedCumulativeData,
				categoryData: processedCategoryData,
				searchTermsProcessed: Object.keys(searchTermResults).length,
				responseFormat: data.body ? "legacy_with_body" : "new_direct_keys",
			},
			originalResponse: data,
		};

		console.log("✅ ===== NORMALIZATION COMPLETE =====");
		console.log("✅ Final normalized response structure:", {
			audiencesCount: normalizedAudiences.length,
			uniqueCount: normalizedAudiences.length,
			totalCount,
			searchTermsProcessed: Object.keys(searchTermResults).length,
			searchTerms: Object.keys(searchTermResults),
			responseFormat: normalizedResponse.metadata.responseFormat,
		});

		return normalizedResponse;
	}

	normalizeAudienceItem(audience) {
		if (!audience) return null;

		// Extract audienceVector and audienceMetrics
		const audienceVector = audience.audienceVector || {};
		const audienceMetrics = audience.audienceMetrics || {};

		const normalizedItem = {
			// Basic identifiers
			audienceId:
				audienceVector.audienceId || audience.id || audience.audience_id,
			audienceName:
				audienceVector.cleanAudienceName ||
				audienceVector.audienceName ||
				audience.name ||
				audience.audience_name ||
				"Unknown Audience",
			accountName:
				audienceVector.accountName ||
				audience.account_name ||
				"Unknown Account",
			description:
				audienceVector.description ||
				audienceVector.notesDescription ||
				audience.description ||
				"N/A",

			// Scores from the main audience object
			compositeScore: audience.compositeScore || 0,
			score: audience.score || 0,

			// Audience properties from audienceVector
			type: audienceVector.type || "N/A",
			dataSource:
				audienceVector.dataSource || audienceVector.data_source || "N/A",
			region: audienceVector.region || "N/A",
			status: audienceVector.status || "UNKNOWN",
			audienceStrategy: audienceVector.audienceStrategy || "N/A",

			// Technical attributes
			exportTargets: (() => {
				const targets = audienceVector.exportTargets;
				if (!targets) return "N/A";
				if (Array.isArray(targets)) return targets.join(", ");
				if (typeof targets === "string")
					return targets.replace(/[[\]"]/g, "").replace(/,/g, ", ");
				return String(targets);
			})(),
			technicalAttributes: audienceVector.technicalAttributes || "N/A",
			createdWith: audienceVector.createdWith || "N/A",

			// Metrics from audienceMetrics and audienceVector
			metrics: {
				// Reach metrics
				estimatedReach:
					audienceVector.estimatedReach || audience.estimated_reach || 0,
				reach: audienceVector.reach || audienceMetrics.reach || 0,

				// Performance metrics
				requests: audienceMetrics.requests || 0,
				soldImpressions:
					audienceMetrics.soldImpressions ||
					audienceMetrics.sold_impressions ||
					0,
				uniqueUsers:
					audienceMetrics.uniqueUsers || audienceMetrics.unique_users || 0,
				avgEcpm: audienceMetrics.avgEcpm || audienceMetrics.avg_ecpm || 0,
				clickThroughRate:
					audienceMetrics.clickThroughRate || audienceMetrics.ctr || 0,
				viewability: audienceMetrics.viewability || 0,
				impressionFrequency:
					audienceMetrics.impressionFrequency ||
					audienceMetrics.impression_frequency ||
					0,

				// Device metrics
				mobileDevices:
					audienceMetrics.mobileDevices || audienceMetrics.mobile_devices || 0,
				tabletDevices:
					audienceMetrics.tabletDevices || audienceMetrics.tablet_devices || 0,
				connectedTvDevices:
					audienceMetrics.connectedTvDevices ||
					audienceMetrics.connectedtv_devices ||
					0,
				households: audienceMetrics.households || 0,

				// ID linking metrics
				cookies: audienceMetrics.cookies || 0,
				id5s: audienceMetrics.id5s || 0,

				// Financial metrics
				advertiserSpend:
					audienceMetrics.advertiserSpend ||
					audienceMetrics.advertiser_spend ||
					0,
				avgSpendPerUniqueUser: audienceMetrics.avgSpendPerUniqueUser || 0,

				// Performance metrics
				bidGuidance:
					audienceMetrics.bidGuidance || audienceMetrics.bid_guidance || 0,
				oxWinRate:
					audienceMetrics.oxWinRate || audienceMetrics.ox_win_rate || 0,
				pubWinRate:
					audienceMetrics.pubWinRate || audienceMetrics.pub_win_rate || 0,
			},

			// Additional data
			createdAt: audienceVector.createdAt || audienceVector.created_at,
			updatedAt: audienceVector.updatedDate || audienceVector.updated_date,
			tags: Array.isArray(audienceVector.tags) ? audienceVector.tags : [],
			isArchived:
				audienceVector.isArchived || audienceVector.is_archived || false,
			hasMeta: audienceVector.hasMeta || audienceVector.has_meta || false,
			shareableOpenxAudience:
				audienceVector.shareableOpenxAudience ||
				audienceVector.shareable_openx_audience ||
				false,

			// Technical info
			jobId: audienceVector.jobId || audienceVector.job_id,
			bqTable: audienceVector.bqTable || audienceVector.bq_table,
			pricing: audienceVector.pricing,

			// Segment data if available
			segmentData: audience.segmentData || null,

			// Keep original data for advanced use cases
			originalData: audience,
			audienceVector: audienceVector,
			audienceMetrics: audienceMetrics,
		};

		return normalizedItem;
	}

	processCumulativeData(cumulativeData) {
		if (!cumulativeData || typeof cumulativeData !== "object") {
			return null;
		}

		const processedData = {};

		Object.keys(cumulativeData).forEach((key) => {
			const data = cumulativeData[key];
			if (data && typeof data === "object") {
				processedData[key] = {
					audienceIds: data.audienceIds
						? data.audienceIds.split(",").filter((id) => id.trim())
						: [],
					noOfAudience: data.noOfAudience || 0,
					reach: data.reach || 0,
				};
			}
		});

		return processedData;
	}

	processCategoryData(categoryData) {
		if (!Array.isArray(categoryData)) {
			return null;
		}

		return categoryData.map((category) => ({
			categoryType: category.categoryType || "UNKNOWN",
			audienceIds: category.audienceIds
				? category.audienceIds.split(",").filter((id) => id.trim())
				: [],
		}));
	}

	async healthCheck() {
		try {
			if (!this.isAuthenticated()) {
				return {
					success: false,
					status: 401,
					error: "Authentication required",
					timestamp: new Date().toISOString(),
				};
			}

			const response = await this.makeRequest("/audience/health");

			return {
				success: response.success,
				status: response.statusCode || 200,
				timestamp: new Date().toISOString(),
				message: response.success
					? "Audience API connection successful"
					: response.error,
			};
		} catch (error) {
			return {
				success: false,
				error: error.message,
				timestamp: new Date().toISOString(),
			};
		}
	}

	async testConnection() {
		try {
			if (!this.isAuthenticated()) {
				return {
					success: false,
					message: "Authentication required - redirected to login",
					authenticated: false,
				};
			}

			const response = await this.makeRequest("/health");
			return {
				success: response.success,
				message: response.success
					? "Audience API connection successful"
					: response.error,
				authenticated: this.isAuthenticated(),
			};
		} catch (error) {
			return {
				success: false,
				message: error.message,
				authenticated: this.isAuthenticated(),
			};
		}
	}

	getAuthStatus() {
		const mainServiceAuth = window.apiService
			? {
					available: true,
					authenticated: window.apiService.isAuthenticated
						? window.apiService.isAuthenticated()
						: false,
					user: window.apiService.getUser ? window.apiService.getUser() : null,
				}
			: { available: false };

		return {
			isAuthenticated: this.isAuthenticated(),
			user: this.getUser(),
			hasSharedToken: !!this.getSharedAuthToken(),
			mainApiService: mainServiceAuth,
		};
	}

	/**
	 * 🆕 Clear all caches (including new segment cache)
	 */
	clearCache() {
		this.cache.clear();
		this.clearFilterValuesCache();
		this.clearSegmentCache(); // 🆕 Clear segment cache
		console.log("🧹 All Audience API caches cleared");
	}

	/**
	 * 🆕 Get comprehensive cache status (including segment cache)
	 */
	getCacheStatus() {
		const cacheEntries = Array.from(this.cache.entries()).map(
			([key, value]) => ({
				key,
				age: Date.now() - value.timestamp,
				size: JSON.stringify(value.data).length,
			}),
		);

		const filterCacheStatus = this.getFilterValuesCacheStatus();
		const segmentCacheStatus = this.getSegmentCacheStatus(); // 🆕 Include segment cache

		return {
			entries: cacheEntries.length,
			details: cacheEntries,
			filterValuesCache: filterCacheStatus,
			segmentCache: segmentCacheStatus, // 🆕 Include segment cache status
		};
	}
}

// Create and export singleton instance
const audienceApiService = new AudienceApiService();

// Global access for debugging
window.audienceApiService = audienceApiService;

console.log("💥 AudienceApiService with Segment API loaded");

export default audienceApiService;
export { AudienceApiService };
